import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import { StudentLayout } from '../../components/layout/StudentLayout';
import { studentExamApi } from '../../api/examApi';
import {
  useExamAccess,
  ExamAccessBanner,
  ExamAccessBlockedButton,
} from '../../components/student/ExamAccessGate';
import { toast } from 'sonner';
import { PaperTestBadge, PaperTestAction } from '../../components/student/PaperTestStatus';

const STATUS_CFG = {
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  submitted:   { label: 'Submitted',   color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  evaluated:   { label: 'Evaluated',   color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
};

export default function StudentExamsPage() {
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const access = useExamAccess();

  useEffect(() => {
    Promise.all([
      studentExamApi.getAvailableExams(),
      studentExamApi.getMyAttempts(),
    ])
      .then(([examsRes, attemptsRes]) => {
        setExams(examsRes.data);
        setAttempts(attemptsRes.data);
      })
      .catch(() => toast.error('Failed to load exams'))
      .finally(() => setLoading(false));
  }, []);

  const getAttempt = (examId) => attempts.find((a) => a.exam_id === examId);

  return (
    <StudentLayout title="My Exams">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Assigned Exams</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {exams.length} exam{exams.length !== 1 ? 's' : ''} assigned to you
          </p>
        </div>

        {!access.loading && !access.allowed && <ExamAccessBanner status={access.status} />}

        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-700" />
            ))}
          </div>
        ) : exams.length === 0 ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-12 text-center">
            <BookOpen size={40} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-slate-500 dark:text-slate-400">No exams assigned yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {exams.map((exam) => {
              const attempt = getAttempt(exam.id);
              const statusKey = attempt?.status || 'not_started';
              const cfg = STATUS_CFG[statusKey] || STATUS_CFG.not_started;
              const isPaper = exam.conduct_mode === 'offline';
              const canStart = !isPaper && (!attempt || attempt.status === 'in_progress');

              return (
                <div
                  key={exam.id}
                  className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 hover:border-primary/50 transition-colors"
                  data-testid={`exam-row-${exam.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-medium text-slate-900 dark:text-white">{exam.title}</h3>
                      {isPaper ? (
                        <PaperTestBadge exam={exam} />
                      ) : (
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                      <span>{exam.subject}</span>
                      <span>·</span>
                      <span>{isPaper ? `Paper test${exam.exam_date ? ` · ${exam.exam_date}` : ''}` : `${exam.duration_minutes} min`}</span>
                      <span>·</span>
                      <span>{exam.total_marks} marks</span>
                    </div>
                    {attempt?.total_score != null && (
                      <p className="mt-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        Score: {attempt.total_score}/{exam.total_marks}
                      </p>
                    )}
                  </div>

                  {isPaper ? (
                    <PaperTestAction exam={exam} />
                  ) : canStart && !access.allowed ? (
                    <ExamAccessBlockedButton />
                  ) : canStart ? (
                    <Link
                      to={
                        attempt
                          ? `/student/exam/${exam.id}/attempt/${attempt.id}`
                          : `/student/exam/${exam.id}`
                      }
                      className="flex items-center gap-1 rounded-lg bg-primary hover:bg-primary-light px-4 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap"
                    >
                      {attempt ? 'Continue' : 'Start'} <ChevronRight size={15} />
                    </Link>
                  ) : (
                    <Link
                      to={`/student/results/${attempt.id}`}
                      className="flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
                    >
                      View Result <ChevronRight size={15} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
